/** Card — the most-reused surface across NVL.
 *  Variants: default (surface), elevated (glass + shadow), bare (no border).
 *  Composition: <Card><CardMedia/><CardBody><CardMeta/><CardTitle/><CardDesc/><CardFoot/></CardBody></Card>
 */
import { forwardRef } from "react";
import { cn } from "../../lib/cn";

export const Card = forwardRef(function Card(
  { className, variant = "default", interactive = false, as: As = "div", ...props },
  ref
) {
  const base = "relative rounded-[22px] overflow-hidden transition-[transform,border-color,box-shadow] duration-500 ease-nvl";
  const variants = {
    default: "bg-surface border border-line",
    elevated: "bg-bg-elev border border-line-2 shadow-card",
    glass: "bg-glass border border-line-2 backdrop-blur-xl",
    bare: "bg-transparent",
  };
  const hover = interactive ? "hover:-translate-y-1 hover:border-mauve/60 hover:shadow-card cursor-pointer" : "";
  return <As ref={ref} className={cn(base, variants[variant], hover, className)} {...props} />;
});

export function CardMedia({ className, children, ...props }) {
  return (
    <div className={cn("relative aspect-[16/10] overflow-hidden border-b border-line", className)} {...props}>
      {children}
    </div>
  );
}

export function CardBody({ className, ...props }) {
  return <div className={cn("p-6", className)} {...props} />;
}

export function CardMeta({ className, children, ...props }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 text-[12.5px] text-text-dim font-mono uppercase tracking-[0.16em]",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardTitle({ className, children, as: As = "h3", ...props }) {
  return (
    <As
      className={cn(
        "font-display text-[22px] leading-tight tracking-[-0.01em] text-text",
        className
      )}
      {...props}
    >
      {children}
    </As>
  );
}

export function CardDesc({ className, children, ...props }) {
  return <p className={cn("text-text-dim text-[14px] leading-relaxed", className)} {...props}>{children}</p>;
}

export function CardFoot({ className, ...props }) {
  return (
    <div
      className={cn("flex items-center justify-between gap-3 pt-5 mt-5 border-t border-line", className)}
      {...props}
    />
  );
}

export function CardTag({ className, children, ...props }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.16em] bg-glass-2 backdrop-blur-md border border-line-2 text-text",
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}
